import { Skeleton, Stack, Typography } from '@mui/material'
import { FC, useCallback, useState } from 'react'
import { GRAY_900 } from 'constants/styling/theme'
import { IntegrationDTO } from 'models/integration'

import { ConnectIntegrationModal } from './ConnectIntegrationModal'
import { IntegrationCard } from './IntegrationCard'
import { useActionPopup, useWorkspaceRoles } from 'utils/hooks'
import { useDisconnectIntegration, useGetIntegrations } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const IntegrationsModule: FC = () => {
  const { t } = useTranslation(['integrations'])
  const { userPropertyWorkspaceId } = useUserData()
  const { isPropstackLinked } = useWorkspaceRoles()
  const { data: integrations, isLoading } = useGetIntegrations(userPropertyWorkspaceId, isPropstackLinked)
  const { showConfirm } = useActionPopup()
  const disconnectMutation = useDisconnectIntegration()

  // Connect modal state
  const [connectModalOpen, setConnectModalOpen] = useState(false)
  const [selectedIntegrationForConnect, setSelectedIntegrationForConnect] = useState<IntegrationDTO | null>(null)

  const handleConnect = useCallback((integration: IntegrationDTO) => {
    setSelectedIntegrationForConnect(integration)
    setConnectModalOpen(true)
  }, [])

  const handleCloseConnectModal = useCallback(() => {
    setConnectModalOpen(false)
    setSelectedIntegrationForConnect(null)
  }, [])

  const handleDisconnect = useCallback(async (integration: IntegrationDTO) => {
    // Synthetic Propstack bindings have no BE record — DELETE_CREDENTIALS would 404
    if (integration.isSyntheticBinding) return

    const confirmed = await showConfirm(
      t('integrations:settings_modal.confirm_disconnect_message', { name: t(integration.name) }),
      {
        title: t('integrations:settings_modal.confirm_disconnect_title'),
        confirmAcceptText: t('integrations:settings_modal.confirm_disconnect'),
        confirmDenyText: t('integrations:settings_modal.cancel'),
      }
    )

    if (confirmed) {
      await disconnectMutation.mutateAsync({ crmType: integration.type, workspaceId: userPropertyWorkspaceId })
    }
  }, [showConfirm, t, disconnectMutation, userPropertyWorkspaceId])

  if (isLoading) {
    return (
      <Stack gap="2.4rem" padding="5.6rem 0">
        <Skeleton variant="text" width="20rem" height="3rem" />
        <Stack direction="row" gap="2.4rem" flexWrap="wrap">
          <Skeleton variant="rounded" width="28.6rem" height="25.2rem" sx={{ borderRadius: '0.8rem' }} />
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack gap="2.4rem" padding="5.6rem 0">
      <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
        {t('integrations:section_title')}
      </Typography>

      <Stack
        direction="row"
        gap="2.4rem"
        flexWrap="wrap"
        sx={{
          '& > *': {
            flex: '0 0 auto',
          },
        }}
      >
        {integrations?.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            onConnect={() => handleConnect(integration)}
            onDisconnect={() => handleDisconnect(integration)}
          />
        ))}
      </Stack>

      {/* Connect Modal */}
      <ConnectIntegrationModal
        open={connectModalOpen}
        integration={selectedIntegrationForConnect}
        onClose={handleCloseConnectModal}
        workspaceId={userPropertyWorkspaceId}
      />
    </Stack>
  )
}
